import React, { useContext, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Routes, Route } from 'react-router-dom';
import CommonLayout, {
  CommonLayoutContent,
  CommonLayoutHeader
} from '@ad/ad_utils_react/src/components/CommonLayout';
import { AppWrapper, renderApp } from '@/utils/createApp';
import { getBaseName } from '@/utils';
import { globalInfoStoreContext } from '@/models/global';
import GlobalHeader from '@/components/GlobalHeader';
import 'moment/min/locales.min.js';
import SharingPage from '@/pages/asset/sharing';
import ImProvider from '@/components/IM/ImProvider';
import { InitOption } from '@/components/IM';
import {
  CC_LOGIN_APP_ID,
  FRONTIER_WS_URL,
  FRONTIER_WS_URL_BOE,
  IMCLOUD_API_URL,
  IMCLOUD_API_URL_BOE
} from '@/utils/const';
import imApis from '@/apis/imApis';
import SharePage from './share';
import { GuestConversationUserInfo, MockUserProvider, useMockUser } from './share/context';
import VideoCommentInfoModalProvider from '@/containers/ProjectDetail/components/OrderContent/OutputTab/VideoCommentInfoModalProvider';
import { observer } from 'mobx-react';
import { useLocalStorage } from 'usehooks-ts';

const hotModule = module as any;
if (hotModule.hot) {
  hotModule.hot.accept();
}

function AssetPage() {
  const globalInfoStore = useContext(globalInfoStoreContext);
  // globalInfoStore.setCurrentUserRole(UserRoleEnum.Advertiser);
  const headerRef = useRef<HTMLDivElement | null>();
  const [guestConversationUserInfo, updateGuestConversationUserInfo] = useLocalStorage<
    GuestConversationUserInfo | undefined
  >('guest_conversation_user_info', undefined);

  const imOptions: InitOption = useMemo(() => {
    const isBoe = window.location.host.indexOf('boe') !== -1;
    return {
      appId: CC_LOGIN_APP_ID, // 星云平台分配的唯一appId
      userId: globalInfoStore.user?.uid || guestConversationUserInfo?.conversationUserId || '', // 用于区分身份、收发消息的唯一id，建议是当前登录用户的userId
      apiUrl: isBoe ? IMCLOUD_API_URL_BOE : IMCLOUD_API_URL,
      frontierUrl: isBoe ? FRONTIER_WS_URL_BOE : FRONTIER_WS_URL,
      // Guest user use getAnonymousChatToken to get IM token
      token: globalInfoStore.loggedIn
        ? imApis.getToken
        : () =>
            imApis.getAnonymousChatToken(
              guestConversationUserInfo?.conversationUserId,
              guestConversationUserInfo?.token
            ),
      timeout: 5000,
      debug: process.env.NODE_ENV === 'development',
      boe: isBoe,
      disabled: globalInfoStore.imDisabled
    };
  }, [
    globalInfoStore.user,
    globalInfoStore.imDisabled,
    guestConversationUserInfo?.conversationUserId,
    globalInfoStore.loggedIn
  ]);

  useLayoutEffect(() => {
    setTimeout(() => {
      // eslint-disable-next-line react/no-find-dom-node
      const headerDOM = ReactDOM.findDOMNode(headerRef.current) as HTMLDivElement;
      if (headerDOM) {
        globalInfoStore.setHeaderHeight(headerDOM?.getBoundingClientRect().height);
      }
    }, 200);
  }, [globalInfoStore.showHeader, globalInfoStore.advHasFreePack]);

  return (
    <ImProvider options={imOptions}>
      <CommonLayout>
        <CommonLayoutHeader fixHeader className="ttcx-page-common-header">
          {/* eslint-disable-next-line no-return-assign */}
          <div ref={(ref) => (headerRef.current = ref)}>
            <GlobalHeader noRole />
          </div>
        </CommonLayoutHeader>
        <CommonLayoutContent>
          <Routes>
            <Route
              path="/share/*"
              element={
                <VideoCommentInfoModalProvider>
                  <SharePage />
                </VideoCommentInfoModalProvider>
              }
            />
            <Route path="/sharing/*" element={<SharingPage />} />
          </Routes>
        </CommonLayoutContent>
      </CommonLayout>
    </ImProvider>
  );
}

const basename = getBaseName('asset');

const AssetWrapperPage = () => {
  return (
    <MockUserProvider isMock>
      <AssetPage />
    </MockUserProvider>
  );
};

renderApp(
  <AppWrapper baseName={basename} title="TikTok Creative Exchange" Component={observer(AssetWrapperPage)} />,
  document.getElementById('root')!
);
